/**
 * Home category showcase: five staggered columns, each showing one category,
 * with the column parallax drift on scroll (IX2 a-48). Hover greyscale/overlay
 * is wired globally in SiteAnimations.
 */
import type { FC } from 'react'
import type { Category } from '@/types'

import { useEffect, useRef } from 'react'
import Link from 'next/link'

import { gsap } from '@/lib/gsap'

interface CategoryShowcaseProps {
    categories: Category[]
}

const DRIFT: Array<[number, number]> = [
    [-5, 5],
    [-10, 10],
    [-5, 5],
    [-10, 10],
    [-5, 5]
]

export const CategoryShowcase: FC<CategoryShowcaseProps> = ({ categories }) => {
    const sectionRef = useRef<HTMLDivElement>(null)
    const columns = categories.slice(0, 5)

    useEffect(() => {
        const section = sectionRef.current
        if (!section) return

        const ctx = gsap.context(() => {
            DRIFT.forEach(([from, to], index) => {
                const target = section.querySelector(`.category-list-wrapper._0${index + 1}`)
                if (!target) return

                gsap.fromTo(
                    target,
                    { yPercent: from },
                    {
                        yPercent: to,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: section,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: true
                        }
                    }
                )
            })
        }, section)

        return () => ctx.revert()
    }, [])

    return (
        <div ref={sectionRef} className='section'>
            <div className='container categroy-container'>
                <div className='category-holder'>
                    <div className='category-list'>
                        {columns.map((category, index) => (
                            <div key={category.slug} className={`category-list-wrapper _0${index + 1} w-dyn-list`}>
                                <div role='list' className='category-list w-dyn-items'>
                                    <div role='listitem' className='category-item w-dyn-item'>
                                        <Link href={`/category/${category.slug}`} className='category-card-container w-inline-block'>
                                            <div className='category-image-container'>
                                                <div className='category-overlay' />
                                                <img src={category.image} loading='lazy' alt={category.name} className='category-image' />
                                            </div>
                                            <div className='category-text-holder'>
                                                <div className='category-name'>
                                                    <h2 className='category-text'>{category.name}</h2>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
